/**
 * Format a booked-session slot like "Tue, Feb 10, 2026 at 9:00 AM – 9:30 AM".
 * Defensive — falls back to the raw strings if the date/time can't be parsed.
 *
 * Shared by the DM expert-chat and the per-booking ritual group chat surfaces
 * since both render the same "Your session with {expert} is booked for ..."
 * banner.
 */
export interface ChatBookingSlot {
  date: string
  startTime: string
  endTime: string
}

export function formatBookingSlot(ctx: ChatBookingSlot): string {
  try {
    const date = new Date(`${ctx.date}T${ctx.startTime}`)
    if (Number.isNaN(date.getTime())) throw new Error("invalid date")
    const dateStr = date.toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    })
    const startStr = formatTime(ctx.startTime)
    const endStr = formatTime(ctx.endTime)
    return `${dateStr} at ${startStr} – ${endStr}`
  } catch {
    return `${ctx.date} at ${ctx.startTime} – ${ctx.endTime}`
  }
}

function formatTime(hhmm: string): string {
  const [hStr, mStr] = hhmm.split(":")
  const h = Number.parseInt(hStr, 10)
  const m = Number.parseInt(mStr, 10)
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmm
  const period = h >= 12 ? "PM" : "AM"
  const hour12 = h % 12 === 0 ? 12 : h % 12
  return `${hour12}:${m.toString().padStart(2, "0")} ${period}`
}
